import { PWA_ACCEPTED_SESSION_SURFACES } from "./sessionSurface";
import { PUBLIC_TRIAL_SECONDS } from "@/app/publicTrialPolicy";
import { accountApiBase, withConnectionAuth } from "@/app/connection";
import type { PersonalApp } from "@/components/personal-apps/types";
import { portableBlobSha256 } from "./portableBlobDigest";

const MAX_BUNDLE_BYTES = 256 * 1024 * 1024;
const MAX_WORKER_UPLOAD_BYTES = 32 * 1024 * 1024;
const MAX_INDEX_BYTES = 8 * 1024 * 1024;
const VERIFICATION_STORAGE_PREFIX = "ato:portable-verification:";

export class PortableApplicationError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message = code,
    readonly retryAfterSeconds?: number,
    readonly activeAppId?: string,
    readonly allocationNone = false,
  ) {
    super(message);
    this.name = "PortableApplicationError";
  }
}

export interface ContractVerificationReceipt {
  schema: "ato.contract-verification-receipt/1";
  bundle_sha256: string;
  contract_ref: string;
  derivation_ref: string;
  target: { kind: "ato-run-hosted" };
  observations: Array<{
    id: string;
    outcome: "satisfied" | "deferred" | "failed";
    evidence?: Record<string, unknown>;
    failure?: string;
  }>;
  fully_satisfied: boolean;
  execution?: {
    realization: string;
    runtime_executable?: string;
    runtime_version?: string;
    pid?: number;
    container_id?: string;
    image?: string;
    platform?: string;
    endpoint?: string;
    run_id?: string;
    lease_id?: string;
    attempt_id?: string;
  };
}

export type PortableRealizationSelection =
  | "static_web"
  | "process"
  | "oci"
  | "oci_service_group";
export type PortablePlacement =
  | { kind: "managed"; compute_account?: "operator" | "owner" }
  | { kind: "user_runner"; runner_id: string };

export interface PortableDerivationCandidate {
  derivation_ref: string;
  realization: PortableRealizationSelection;
  runtimes: Record<string, string>;
}

export type PortableDerivationResolver = (
  candidates: PortableDerivationCandidate[],
  signal: AbortSignal,
) => Promise<string>;

export interface PortableBindingRequirement {
  id: string;
  /** Legacy computation-root reports used `schema`; portable Application
   * reports name the protocol directly. */
  schema?: string;
  protocol?: string;
  required?: boolean;
  environment?: string;
}

export type PortableBindingResolver = (
  requirements: PortableBindingRequirement[],
  signal: AbortSignal,
) => Promise<Record<string, string>>;

export interface PortableApplicationVerificationSummary {
  kind: "ato-app-file";
  file_name: string;
  bundle_sha256: string;
  contract_ref: string;
  realization:
    | "Static Web"
    | "Python process"
    | "OCI container"
    | "OCI service group";
  derivation_ref: string;
  verified: true;
  surface_status?: "ready";
  instance_snapshot_ref?: string;
  saved_data_status?: "restored";
}

export function rememberPortableApplicationVerification(
  instanceId: string,
  fileName: string,
  receipt: ContractVerificationReceipt,
  instanceSnapshotRef?: string | null,
): void {
  const summary: PortableApplicationVerificationSummary = {
    kind: "ato-app-file",
    file_name: fileName,
    bundle_sha256: receipt.bundle_sha256,
    contract_ref: receipt.contract_ref,
    realization:
      receipt.execution?.realization === "process"
        ? "Python process"
        : receipt.execution?.realization === "oci"
          ? "OCI container"
          : receipt.execution?.realization === "oci_service_group"
            ? "OCI service group"
            : "Static Web",
    derivation_ref: receipt.derivation_ref,
    verified: true,
    surface_status: "ready",
    ...(instanceSnapshotRef
      ? {
          instance_snapshot_ref: instanceSnapshotRef,
          saved_data_status: "restored" as const,
        }
      : {}),
  };
  try {
    window.sessionStorage.setItem(
      `${VERIFICATION_STORAGE_PREFIX}${instanceId}`,
      JSON.stringify(summary),
    );
  } catch {
    // The receipt remains persisted by the API. This cache only carries the
    // just-completed verification into the debug summary on the Ready page.
  }
}

export function portableApplicationVerificationFor(
  instanceId: string,
): PortableApplicationVerificationSummary | null {
  try {
    const raw = window.sessionStorage.getItem(
      `${VERIFICATION_STORAGE_PREFIX}${instanceId}`,
    );
    if (!raw) return null;
    const value = JSON.parse(
      raw,
    ) as Partial<PortableApplicationVerificationSummary>;
    if (
      value.kind !== "ato-app-file" ||
      typeof value.file_name !== "string" ||
      !/^sha256:[0-9a-f]{64}$/.test(value.bundle_sha256 ?? "") ||
      !/^sha256:[0-9a-f]{64}$/.test(value.contract_ref ?? "") ||
      ![
        "Static Web",
        "Python process",
        "OCI container",
        "OCI service group",
      ].includes(value.realization ?? "") ||
      !/^sha256:[0-9a-f]{64}$/.test(value.derivation_ref ?? "") ||
      value.verified !== true
    ) {
      return null;
    }
    if (
      value.instance_snapshot_ref !== undefined &&
      (!/^sha256:[0-9a-f]{64}$/.test(value.instance_snapshot_ref) ||
        value.saved_data_status !== "restored")
    ) {
      return null;
    }
    return value as PortableApplicationVerificationSummary;
  } catch {
    return null;
  }
}

export type ImportStatus = {
  import: {
    bundle_id: string;
    bundle_sha256: string;
    root_contract_ref: string;
    application_ref: string;
    selected_derivation_ref: string;
    placement?: PortablePlacement | null;
    run_id: string | null;
    lease_id: string | null;
    instance_snapshot_ref: string | null;
    snapshot_restored_at: string | null;
    session_kind?: "durable" | "public_try";
    expires_at?: string | null;
    public_trial_limit_seconds?: number | null;
    stopped_at?: string | null;
    public_access_available?: boolean;
    setup_required?: {
      code: "tcp_egress_grant_required" | "runner_provisioning";
    } | null;
    verification_status: "pending" | "verified" | "failed";
    verification_receipt: ContractVerificationReceipt | null;
    surface: {
      status: "starting" | "ready" | "failed";
      run_id: string | null;
      lease_id: string | null;
      reason?: string;
    };
    instance: PersonalApp;
  };
};

/** Explicitly resumes the existing prepared import; no alternate launch path. */
export async function launchPreparedApplication(
  prepared: import("@/components/personal-apps/types").PreparedImport,
  signal?: AbortSignal,
): Promise<ImportStatus | ImportRequestStatus> {
  return postPortableImport(
    `/v1/capsule-bundles/${encodeURIComponent(prepared.bundle_id)}/import-app`,
    {
      method: "POST",
      signal,
      headers: {
        "Content-Type": "application/json",
        "Idempotency-Key": prepared.idempotency_key,
      },
      body: JSON.stringify({
        start: true,
        accepted_session_surfaces: PWA_ACCEPTED_SESSION_SURFACES,
        derivation_ref: prepared.derivation_ref,
        placement: prepared.placement,
      }),
    },
  );
}

type ImportRequestStatus = {
  import_request: {
    request_id: string;
    status: "preparing";
  };
};

export type PortableImportRecovery =
  | { status: "pending" }
  | { status: "allocated"; app: PersonalApp }
  | { status: "unallocated"; failureCode: string };

type UnallocatedImportStatus = {
  import_request: {
    status: "failed";
    allocation: "none";
    processing_finished: true;
    failure_code: string;
  };
};

function isImportStatus(
  value: ImportStatus | ImportRequestStatus,
): value is ImportStatus {
  return "import" in value;
}

type Progress = (
  phase: "uploading" | "building" | "finishing",
  message: string,
) => void;

function api(path: string): string {
  return `${accountApiBase()}${path}`;
}

async function json<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(
    api(path),
    withConnectionAuth({ cache: "no-store", ...init }),
  );
  const payload = (await response.json().catch(() => ({}))) as {
    error?: string;
    message?: string;
    retry_after_seconds?: number;
    active_app_id?: string;
    allocation?: string;
  };
  if (!response.ok) {
    throw new PortableApplicationError(
      response.status,
      payload.error ?? `http_${response.status}`,
      payload.message ?? payload.error ?? `http_${response.status}`,
      payload.retry_after_seconds,
      payload.active_app_id,
      response.status === 429 && payload.allocation === "none",
    );
  }
  return payload as T;
}

/**
 * Production can run an older API whose strict import schema rejects
 * `accepted_session_surfaces` (it was added to this client before the API).
 * Zod rejects the body before the handler runs, so dropping only that field and
 * resending once is safe. Every other error is surfaced unchanged, and no other
 * mutation is retried implicitly.
 */
function rejectsAcceptedSessionSurfaces(error: unknown): boolean {
  if (!(error instanceof PortableApplicationError) || error.status !== 400) {
    return false;
  }
  // An older API returns Zod's object-valued error directly as `code`.
  // Check the serialized issues, not `name`: JSON drops non-enumerable ones.
  const zodError = error.code as unknown as { issues?: unknown };
  if (
    !zodError ||
    typeof zodError !== "object" ||
    !Array.isArray(zodError.issues)
  ) {
    return false;
  }
  return zodError.issues.some((issue) => {
    const candidate = issue as { code?: unknown; keys?: unknown };
    return (
      candidate.code === "unrecognized_keys" &&
      Array.isArray(candidate.keys) &&
      candidate.keys.includes("accepted_session_surfaces")
    );
  });
}

function postPortableImport<T>(path: string, init: RequestInit): Promise<T> {
  async function send(body: string): Promise<T> {
    return json<T>(path, { ...init, body });
  }
  const body = String(init.body);
  return send(body).catch((error: unknown) => {
    if (!rejectsAcceptedSessionSurfaces(error)) throw error;
    const compatible = JSON.parse(body) as Record<string, unknown>;
    delete compatible.accepted_session_surfaces;
    return send(JSON.stringify(compatible));
  });
}

function portableFormat(file: File): Promise<{
  format_version: 3 | 4;
  profile: "ato.portable-application/1" | "ato.portable-application/2";
}> {
  return file
    .slice(0, MAX_INDEX_BYTES)
    .text()
    .then((prefix) => {
      const marker = '{"index":';
      if (!prefix.startsWith(marker)) {
        throw new PortableApplicationError(422, "bundle_index_missing");
      }
      const start = marker.length;
      let depth = 0;
      let quoted = false;
      let escaped = false;
      for (let index = start; index < prefix.length; index += 1) {
        const character = prefix[index];
        if (quoted) {
          if (escaped) escaped = false;
          else if (character === "\\") escaped = true;
          else if (character === '"') quoted = false;
        } else if (character === '"') quoted = true;
        else if (character === "{") depth += 1;
        else if (character === "}" && --depth === 0) {
          let envelope: { version?: unknown; profile?: unknown };
          try {
            envelope = JSON.parse(prefix.slice(start, index + 1));
          } catch {
            throw new PortableApplicationError(422, "bundle_index_invalid");
          }
          if (
            envelope.version === 3 &&
            envelope.profile === "ato.portable-application/1"
          ) {
            return { format_version: 3, profile: envelope.profile };
          }
          if (
            envelope.version === 4 &&
            envelope.profile === "ato.portable-application/2"
          ) {
            return { format_version: 4, profile: envelope.profile };
          }
          throw new PortableApplicationError(422, "unsupported_profile");
        }
      }
      throw new PortableApplicationError(422, "bundle_index_too_large");
    });
}

async function wait(signal: AbortSignal, milliseconds = 500): Promise<void> {
  await new Promise<void>((resolve, reject) => {
    const timer = window.setTimeout(resolve, milliseconds);
    signal.addEventListener(
      "abort",
      () => {
        window.clearTimeout(timer);
        reject(new DOMException("Aborted", "AbortError"));
      },
      { once: true },
    );
  });
}

async function importValidatedPortableBundle(
  input: {
    importIdempotencyKey?: string;
    expectedContractRef?: string;
    bundleId: string;
    bundleSha256: string;
    derivationRef: string;
    fileName: string;
    bindings?: Record<string, string>;
    placement?: PortablePlacement;
    publicTry?: boolean;
    trialLimitSeconds?: number | null;
    publicGuest?: boolean;
    onAllocated?: (app: PersonalApp) => void;
    onPending?: (recover: () => Promise<PortableImportRecovery>) => void;
  },
  progress: Progress,
  signal: AbortSignal,
): Promise<{
  app: PersonalApp;
  receipt: ContractVerificationReceipt;
  instanceSnapshotRef: string | null;
  expiresAt: string | null;
}> {
  const placement = input.placement ?? { kind: "managed" };
  progress(
    "finishing",
    "Opening app…",
  );
  let deadline = Date.now() + (placement.kind === "managed" && placement.compute_account ? 12 * 60_000 : 180_000);
  const importPath = `/v1/capsule-bundles/${input.bundleId}/${input.publicGuest ? "public-try" : "import-app"}`;
  if (input.publicGuest)
    await json(
      `${importPath}/identity?derivation_ref=${encodeURIComponent(input.derivationRef)}`,
      { signal },
    );
  const importIdempotencyKey = input.importIdempotencyKey ?? `portable-import:${crypto.randomUUID()}`;
  const importBody = JSON.stringify({
    accepted_session_surfaces: PWA_ACCEPTED_SESSION_SURFACES,
    derivation_ref: input.derivationRef,
    ...(placement.kind === "user_runner" || placement.compute_account ? { placement } : {}),
    ...(input.publicTry
      ? { session: { kind: "public_try", ttl_seconds: input.trialLimitSeconds === null ? 0 : input.trialLimitSeconds ?? PUBLIC_TRIAL_SECONDS } }
      : {}),
    ...(input.bindings && Object.keys(input.bindings).length > 0
      ? { bindings: input.bindings }
      : {}),
  });
  const pollQuery = input.publicTry ? "&resume_public_try=1" : "";
  if (input.publicTry)
    input.onPending?.(async () => {
      const result = await json<
        ImportStatus | ImportRequestStatus | UnallocatedImportStatus
      >(`${importPath}/reconcile`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Idempotency-Key": importIdempotencyKey,
        },
        body: JSON.stringify({
          derivation_ref: input.derivationRef,
          placement,
        }),
      });
      if ("import" in result)
        return {
          status: "allocated",
          app: {
            ...result.import.instance,
            public_try_expires_at: result.import.expires_at ?? null,
            public_try_limit_seconds: result.import.public_trial_limit_seconds,
          },
        };
      const request = result.import_request;
      if (
        request.status === "failed" &&
        request.allocation === "none" &&
        request.processing_finished === true
      )
        return { status: "unallocated", failureCode: request.failure_code };
      return { status: "pending" };
    });
  // The POST is not tied to the caller's signal. Pausing only stops this page
  // from waiting; cancelling the request mid-launch could leave a Run that was
  // created but never committed to the import, holding the account's run slot.
  let importResult = await postPortableImport<ImportStatus | ImportRequestStatus>(
    importPath,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Idempotency-Key": importIdempotencyKey,
      },
      body: importBody,
    },
  );
  if (signal.aborted) throw new DOMException("Aborted", "AbortError");
  while (!isImportStatus(importResult)) {
    if (Date.now() >= deadline) {
      throw new PortableApplicationError(408, "portable_import_timeout");
    }
    await wait(signal);
    importResult = await json<ImportStatus | ImportRequestStatus>(
      `${importPath}?derivation_ref=${encodeURIComponent(input.derivationRef)}${pollQuery}`,
      {
        headers: { "Idempotency-Key": importIdempotencyKey },
        signal,
      },
    );
  }
  progress("building", "Preparing your app…");
  let imported = importResult;
  let surfaceStartedAt: number | null = null;
  let waitedForNetworkSetup = false;
  for (;;) {
    input.onAllocated?.({
      ...imported.import.instance,
      public_try_expires_at: imported.import.expires_at ?? null,
      public_try_limit_seconds: imported.import.public_trial_limit_seconds,
    });
    if (signal.aborted) throw new DOMException("Aborted", "AbortError");
    if (
      input.publicTry && imported.import.public_trial_limit_seconds !== null &&
      Date.now() >=
        Date.parse(
          imported.import.expires_at ?? new Date(deadline).toISOString(),
        )
    ) {
      throw new PortableApplicationError(410, "public_try_ended");
    }
    if (imported.import.setup_required?.code === "runner_provisioning") {
      if (Date.now() >= deadline) throw new PortableApplicationError(408, "hosted_verification_timeout");
      progress("finishing", "Preparing your app…");
      await wait(signal, 2_000);
      imported = await postPortableImport<ImportStatus>(importPath, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Idempotency-Key": importIdempotencyKey },
        body: importBody,
        signal,
      });
      continue;
    }
    if (imported.import.setup_required?.code === "tcp_egress_grant_required") {
      waitedForNetworkSetup = true;
      progress("finishing", "Waiting for network access approval…");
      await wait(signal, 2_000);
      imported = await postPortableImport<ImportStatus>(importPath, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Idempotency-Key": importIdempotencyKey,
        },
        body: importBody,
        signal,
      });
      continue;
    }
    if (waitedForNetworkSetup) {
      // Operator approval is intentionally unbounded but remains abortable by
      // the user. Once granted, verification receives its own full window.
      deadline = Date.now() + 180_000;
      waitedForNetworkSetup = false;
    }
    if (
      imported.import.verification_status === "verified" &&
      imported.import.verification_receipt?.fully_satisfied
    ) {
      if (input.publicGuest && !imported.import.public_access_available)
        throw new PortableApplicationError(
          409,
          "discover_public_try_unavailable",
        );
      const receipt = imported.import.verification_receipt;
      if (
        (input.expectedContractRef !== undefined && imported.import.root_contract_ref !== input.expectedContractRef) ||
        imported.import.bundle_sha256 !== input.bundleSha256 ||
        imported.import.selected_derivation_ref !== input.derivationRef ||
        receipt.bundle_sha256 !== input.bundleSha256 ||
        receipt.contract_ref !== imported.import.root_contract_ref ||
        receipt.derivation_ref !== imported.import.selected_derivation_ref
      ) {
        throw new PortableApplicationError(409, "portable_identity_mismatch");
      }
      if (placement.kind === "managed" && placement.compute_account && (imported.import.placement?.kind !== "managed" || imported.import.placement.compute_account !== placement.compute_account)) {
        throw new PortableApplicationError(409, "portable_placement_mismatch");
      }
      if (
        placement.kind === "user_runner" &&
        (imported.import.placement?.kind !== "user_runner" ||
          imported.import.placement.runner_id !== placement.runner_id)
      ) {
        throw new PortableApplicationError(409, "portable_placement_mismatch");
      }
      if (
        !imported.import.surface ||
        imported.import.surface.run_id !== imported.import.run_id ||
        imported.import.surface.lease_id !== imported.import.lease_id
      ) {
        throw new PortableApplicationError(409, "surface_run_mismatch");
      }
      if (imported.import.surface.status === "ready") {
        const instanceSnapshotRef =
          imported.import.instance_snapshot_ref ?? null;
        if (instanceSnapshotRef) {
          const snapshotObservation = receipt.observations.find(
            (observation) => observation.id === "instance-snapshot",
          );
          if (
            !imported.import.snapshot_restored_at ||
            snapshotObservation?.outcome !== "satisfied"
          ) {
            throw new PortableApplicationError(
              409,
              "portable_snapshot_evidence_missing",
            );
          }
        }
        if (!input.publicTry)
          rememberPortableApplicationVerification(
            imported.import.instance.instance_id,
            input.fileName,
            receipt,
            instanceSnapshotRef,
          );
        return {
          app: {
            ...imported.import.instance,
            public_try_expires_at: imported.import.expires_at ?? null,
            public_try_limit_seconds: imported.import.public_trial_limit_seconds,
          },
          receipt,
          instanceSnapshotRef,
          expiresAt: imported.import.expires_at ?? null,
        };
      }
      surfaceStartedAt ??= Date.now();
      const surfaceReason = imported.import.surface.reason;
      if (
        surfaceReason === "surface_route_mismatch" ||
        surfaceReason === "surface_report_missing" ||
        surfaceReason === "surface_run_missing" ||
        Date.now() - surfaceStartedAt >= 30_000
      ) {
        throw new PortableApplicationError(
          503,
          surfaceReason ?? "hosted_surface_failed",
          `Contract: Verified. Surface: Failed (${surfaceReason ?? "unreachable"}).`,
        );
      }
      progress("finishing", "Contract verified. Waiting for the app surface…");
    }
    if (imported.import.verification_status === "pending")
      progress("finishing", "Preparing your app…");
    if (imported.import.verification_status === "failed") {
      throw new PortableApplicationError(422, "hosted_contract_failed");
    }
    if (Date.now() >= deadline) {
      throw new PortableApplicationError(408, "hosted_verification_timeout");
    }
    await wait(signal);
    imported = await json<ImportStatus>(
      `${importPath}?derivation_ref=${encodeURIComponent(input.derivationRef)}${pollQuery}`,
      {
        headers: { "Idempotency-Key": importIdempotencyKey },
        signal,
      },
    );
  }
}

/** Import a validator-approved Discover bundle without re-uploading bytes. */
export async function importHostedPortableApplication(
  target: {
    bundle_id: string;
    bundle_sha256: string;
    contract_ref: string;
    derivation_ref: string;
    file_name: string;
    requires_gpu?: boolean;
  },
  progress: Progress,
  signal: AbortSignal,
  options: {
    importIdempotencyKey?: string;
    resolveGpuPlacement?: (signal: AbortSignal) => Promise<PortablePlacement>;
    publicTry?: boolean;
    trialLimitSeconds?: number | null;
    publicGuest?: boolean;
    onAllocated?: (app: PersonalApp) => void;
    onPending?: (recover: () => Promise<PortableImportRecovery>) => void;
    onProgress?: (message: string) => void;
  } = {},
) {
  if (
    !/^sha256:[0-9a-f]{64}$/.test(target.bundle_sha256) ||
    !/^sha256:[0-9a-f]{64}$/.test(target.contract_ref) ||
    !/^sha256:[0-9a-f]{64}$/.test(target.derivation_ref)
  ) {
    throw new PortableApplicationError(409, "portable_identity_invalid");
  }
  let placement: PortablePlacement = {kind:"managed"};
  if (target.requires_gpu) {
    if (options.publicGuest) throw new PortableApplicationError(409,"discover_public_try_unavailable");
    if (!options.resolveGpuPlacement) throw new PortableApplicationError(409,"gpu_account_selection_required");
    placement = await options.resolveGpuPlacement(signal);
  }
  return importValidatedPortableBundle(
    {
      placement,
      importIdempotencyKey: options.importIdempotencyKey,
      expectedContractRef: target.contract_ref,
      bundleId: target.bundle_id,
      bundleSha256: target.bundle_sha256,
      derivationRef: target.derivation_ref,
      fileName: target.file_name,
      publicTry: options.publicTry,
      trialLimitSeconds: options.trialLimitSeconds,
      publicGuest: options.publicGuest,
      onAllocated: options.onAllocated,
      onPending: options.onPending,
    },
    options.onProgress
      ? (_phase, message) => options.onProgress?.(message)
      : progress,
    signal,
  );
}

export const importDiscoverPortableApplication = importHostedPortableApplication;

/** A Formation result keeps its accepted bundle and D; hosted execution still
 * obtains its own Run-bound Rust verification receipt. */
export async function importFormationPortableApplication(
  result: import("@/components/personal-apps/formationJob").PortableFormationResult,
  jobId: string,
  progress: Progress,
  signal: AbortSignal,
  resolveGpuPlacement?: (signal: AbortSignal) => Promise<PortablePlacement>,
) {
  if (result.formation_kind !== "portable_v2" || !result.fully_satisfied ||
      !result.portable_bundle_artifact_ref || !result.portable_bundle_sha256 ||
      !result.contract_ref || !result.derivation_ref)
    throw new PortableApplicationError(409,"formation_portable_result_invalid");
  const status = await json<{bundle:{validation_status:string;portable_routes?:Array<{derivation_ref:string;requires_gpu?:boolean}>}}>(
    `/v1/capsule-bundles/${encodeURIComponent(result.portable_bundle_artifact_ref)}`, {signal});
  const route=status.bundle.portable_routes?.find(route=>route.derivation_ref===result.derivation_ref);
  if (status.bundle.validation_status !== "ready" || !route)
    throw new PortableApplicationError(409,"formation_portable_bundle_unavailable");
  return importHostedPortableApplication({
    bundle_id:result.portable_bundle_artifact_ref, bundle_sha256:result.portable_bundle_sha256,
    contract_ref:result.contract_ref, derivation_ref:result.derivation_ref,
    file_name:`formation-${jobId}.capsule`, requires_gpu:route.requires_gpu,
  },progress,signal,{resolveGpuPlacement,importIdempotencyKey:`formation-import:${jobId}`});
}

type PortableRouteCandidate = {
  derivation_ref: string;
  realization: PortableRealizationSelection;
  runtimes: Record<string, string>;
};

/**
 * Pick the declared routes that match the Capsule route selector.
 *
 * The selector defaults to Static Web, but a capsule may only declare another
 * realization (for example OCI). Refusing with a generic "derivation missing"
 * leaves the person with no idea that the route selector is the problem, so a
 * single declared realization is used as-is and a mixed bundle reports the
 * realizations it offers.
 */
export function selectPortableCandidates(
  routes: PortableRouteCandidate[],
  realization: PortableRealizationSelection,
  staticDerivationRef: string | null,
): {
  candidates: PortableRouteCandidate[];
  availableRealizations: PortableRealizationSelection[];
} {
  const direct = routes.filter((route) => route.realization === realization);
  if (direct.length > 0) {
    return { candidates: direct, availableRealizations: [] };
  }
  if (realization === "static_web" && staticDerivationRef) {
    return {
      candidates: [
        {
          derivation_ref: staticDerivationRef,
          realization: "static_web",
          runtimes: {},
        },
      ],
      availableRealizations: [],
    };
  }
  const availableRealizations = Array.from(
    new Set(routes.map((route) => route.realization)),
  );
  if (availableRealizations.length === 1) {
    return {
      candidates: routes.filter(
        (route) => route.realization === availableRealizations[0],
      ),
      availableRealizations: [],
    };
  }
  return { candidates: [], availableRealizations };
}

export async function importPortableApplication(
  file: File,
  progress: Progress,
  signal: AbortSignal,
  realization: PortableRealizationSelection = "static_web",
  resolveBindings?: PortableBindingResolver,
  placement: PortablePlacement = { kind: "managed" },
  resolveDerivation?: PortableDerivationResolver,
  resolveGpuPlacement?: (signal: AbortSignal) => Promise<PortablePlacement>,
): Promise<{
  app: PersonalApp;
  receipt: ContractVerificationReceipt;
  instanceSnapshotRef: string | null;
  expiresAt: string | null;
}> {
  if (file.size < 1 || file.size > MAX_BUNDLE_BYTES) {
    throw new PortableApplicationError(413, "bundle_size_invalid");
  }
  progress("uploading", "Checking the capsule…");
  const format = await portableFormat(file);
  if (signal.aborted) throw new DOMException("Aborted", "AbortError");
  const digest = await portableBlobSha256(file, signal);
  type PreparedBundle = {
    bundle_id: string;
    upload_url: string | null;
    upload_direct: boolean;
    upload_required: boolean;
    upload_headers: Record<string, string>;
    validation_status: string;
  };
  const prepare = (idempotencyKey: string) =>
    json<PreparedBundle>("/v1/capsule-bundles/prepare", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Idempotency-Key": idempotencyKey,
      },
      body: JSON.stringify({
        transport_digest: digest,
        size_bytes: file.size,
        ...format,
        allow_direct_upload: file.size > MAX_WORKER_UPLOAD_BYTES,
      }),
      signal,
    });
  let prepared = await prepare(digest);
  if (prepared.validation_status === "rejected") {
    const rejected = await json<{
      bundle: { rejection_code: string | null };
    }>(`/v1/capsule-bundles/${prepared.bundle_id}`, { signal });
    if (rejected.bundle.rejection_code === "validator_failed") {
      prepared = await prepare(
        `${digest}:validator-retry:${crypto.randomUUID()}`,
      );
    }
  }
  if (prepared.upload_required) {
    if (!prepared.upload_url) {
      throw new PortableApplicationError(409, "bundle_upload_url_missing");
    }
    if (file.size > MAX_WORKER_UPLOAD_BYTES && !prepared.upload_direct) {
      throw new PortableApplicationError(413, "direct_upload_required");
    }
    progress("uploading", "Uploading the capsule…");
    const uploaded = await fetch(prepared.upload_url, {
      method: "PUT",
      headers: prepared.upload_headers,
      credentials: prepared.upload_direct ? "omit" : "include",
      body: file,
      signal,
    });
    // A create-only PUT can have succeeded even if its response was lost.
    // Complete still queues server-side verification of the expected digest.
    const createOnlyRetry =
      prepared.upload_direct &&
      uploaded.status === 412 &&
      new Headers(prepared.upload_headers).get("if-none-match") === "*";
    if (!uploaded.ok && !createOnlyRetry) {
      const error = (await uploaded.json().catch(() => ({}))) as {
        error?: string;
      };
      throw new PortableApplicationError(
        uploaded.status,
        error.error ?? `http_${uploaded.status}`,
      );
    }
    if (prepared.upload_direct) {
      await json(`/v1/capsule-bundles/${prepared.bundle_id}/complete`, {
        method: "POST",
        signal,
      });
    }
  }

  progress("building", "Verifying the capsule…");
  const deadline = Date.now() + 180_000;
  let selectedDerivationRef: string | null = null;
  let requiredBindings: PortableBindingRequirement[] = [];
  for (;;) {
    const status = await json<{
      bundle: {
        validation_status: string;
        rejection_code: string | null;
        derivation_refs: string[] | null;
        static_derivation_ref: string | null;
        portable_routes?: Array<{
          derivation_ref: string;
          realization: PortableRealizationSelection;
          runtimes?: Record<string, string>;
          requires_gpu?: boolean;
        }>;
        required_bindings?: PortableBindingRequirement[] | null;
      };
    }>(`/v1/capsule-bundles/${prepared.bundle_id}`, { signal });
    if (status.bundle.validation_status === "ready") {
      const routeCandidates = (status.bundle.portable_routes ?? []).map(
        (route) => ({
          derivation_ref: route.derivation_ref,
          realization: route.realization,
          runtimes: route.runtimes ?? {},
        }),
      );
      const selection = selectPortableCandidates(
        routeCandidates,
        realization,
        status.bundle.static_derivation_ref,
      );
      const candidates = selection.candidates;
      if (
        candidates.length === 0 &&
        selection.availableRealizations.length > 0
      ) {
        throw new PortableApplicationError(
          409,
          "portable_realization_required",
          "This capsule runs as " +
            selection.availableRealizations.join(", ") +
            ". Choose that Capsule route and try again.",
        );
      }
      let selected =
        candidates.length === 1 ? candidates[0].derivation_ref : null;
      if (candidates.length > 1) {
        if (!resolveDerivation) {
          throw new PortableApplicationError(
            409,
            "portable_derivation_selection_required",
          );
        }
        selected = await resolveDerivation(candidates, signal);
        if (signal.aborted) throw new DOMException("Aborted", "AbortError");
        if (
          !candidates.some((candidate) => candidate.derivation_ref === selected)
        ) {
          throw new PortableApplicationError(
            409,
            "portable_selected_derivation_invalid",
          );
        }
      }
      if (
        !selected ||
        !/^sha256:[0-9a-f]{64}$/.test(selected) ||
        !status.bundle.derivation_refs?.includes(selected)
      ) {
        throw new PortableApplicationError(
          409,
          "portable_selected_derivation_missing",
        );
      }
      selectedDerivationRef = selected;
      const selectedRoute = status.bundle.portable_routes?.find(route => route.derivation_ref === selected);
      if (selectedRoute?.requires_gpu && placement.kind === "managed" && !placement.compute_account) {
        if (!resolveGpuPlacement) throw new PortableApplicationError(409,"gpu_account_selection_required");
        placement = await resolveGpuPlacement(signal);
        signal.throwIfAborted();
      }
      // Network grants are operator-managed Instance policy. They are never a
      // password/value the person importing an App can type into this form.
      requiredBindings = (status.bundle.required_bindings ?? []).filter(
        (binding) => binding.protocol !== "ato.tcp-egress@1",
      );
      break;
    }
    if (status.bundle.validation_status === "rejected") {
      throw new PortableApplicationError(
        422,
        status.bundle.rejection_code ?? "bundle_rejected",
      );
    }
    if (Date.now() >= deadline) {
      throw new PortableApplicationError(408, "bundle_validation_timeout");
    }
    await wait(signal);
  }

  let bindings: Record<string, string> = {};
  if (requiredBindings.length > 0) {
    if (!resolveBindings) {
      throw new PortableApplicationError(409, "portable_bindings_required");
    }
    bindings = await resolveBindings(requiredBindings, signal);
    if (signal.aborted) throw new DOMException("Aborted", "AbortError");
  }

  return importValidatedPortableBundle(
    {
      bundleId: prepared.bundle_id,
      bundleSha256: digest,
      derivationRef: selectedDerivationRef,
      fileName: file.name,
      bindings,
      placement,
    },
    progress,
    signal,
  );
}
